import React from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from '@hero/schema';
import { getOrderDisplayFromTo } from '@hero/core';
import { useOrderDestination } from '@hero/hooks-shared';
import { ArrowRightIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { RegionLane, type RegionLaneEntry } from '../../../app/RegionName/RegionLane';
import { InaccurateLocationHint } from './InaccurateLocationHint';

const SIZE_CLASS: Record<string, string> = {
  small: 'text-xs',
  medium: 'text-sm',
};

function toLaneEntry(
  destination: Order['destinations'][number] | undefined,
): RegionLaneEntry | null {
  if (!destination) return null;
  const city = destination.address?.city;
  if (!city?.customCode && !city?.name && !destination.address?.fullAddress) return null;
  return {
    customCode: city?.customCode,
    displayName: city?.name || destination.address?.fullAddress,
  };
}

export interface OrderRouteCompactProps {
  destinations: Order['destinations'];
  order?: Order;
  onClick?: (order: Order) => void;
  className?: string;
  size?: 'small' | 'medium';
  /** Show the pickup date/time under the route line. Defaults to `true` when `order` is provided. */
  showPickupDate?: boolean;
}

/**
 * Compact route cell for order tables.
 *
 * Line 1: "Origin → Destination" via `RegionLane` + a direction-aware
 * arrow (same RTL/LTR behavior as `RouteArrowDisplay`), with amber
 * accuracy hints around cities when a pin is inaccurate:
 * `[i] Origin → Destination [i]` (each side only when that stop is inaccurate).
 * Line 2: pickup date + time window, so the route reads as "where and
 * when" in one glance.
 */
export const OrderRouteCompact: React.FC<OrderRouteCompactProps> = ({
  destinations,
  order,
  onClick,
  className = '',
  size = 'medium',
  showPickupDate = true,
}) => {
  const { t, i18n } = useTranslation(['m-order', 'c-dates']);
  const { firstPickup, lastDropoff } = useOrderDestination(destinations);

  const originEntry = toLaneEntry(firstPickup);
  const destinationEntry = toLaneEntry(lastDropoff);
  const originInaccurate = firstPickup?.isAccurateLocation === false;
  const destinationInaccurate = lastDropoff?.isAccurateLocation === false;

  const isClickable = !!onClick && !!order;

  const handleClick = (e: React.MouseEvent) => {
    if (onClick && order) {
      e.stopPropagation();
      onClick(order);
    }
  };

  if (!originEntry && !destinationEntry) {
    return (
      <span className="text-muted-foreground text-xs">
        {t('m-order:table.columns.not_assigned')}
      </span>
    );
  }

  let pickupDateLine: { date: string; time: string } | undefined;
  if (showPickupDate && order?.dates?.pickup?.from && order?.dates?.pickup?.to) {
    const translateDate = (key: string, fallback?: string): string => {
      if (key === 'date.today') return t('c-dates:relative_dates.today');
      if (key === 'date.tomorrow') return t('c-dates:relative_dates.tomorrow');
      return t(key, { defaultValue: fallback || key });
    };
    const dateDisplay = getOrderDisplayFromTo({
      from: order.dates.pickup.from,
      to: order.dates.pickup.to,
      translate: translateDate,
      isRTL: i18n.dir() === 'rtl',
    });
    pickupDateLine = {
      date: dateDisplay.dateAutoOnly,
      time: dateDisplay.timeCompactOnly,
    };
  }

  const routeStops = [originEntry, destinationEntry].filter(
    (entry): entry is RegionLaneEntry => entry !== null,
  );

  const routeArrow = (
    <ArrowRightIcon
      className={cn(
        'text-gray-400 flex-shrink-0 rtl:rotate-180 transition-colors',
        isClickable && 'group-hover/route:text-primary/70',
      )}
      style={{ width: '1em', height: '1em' }}
      strokeWidth={2.5}
    />
  );

  return (
    <div
      className={cn(
        'flex w-full min-w-0 max-w-full flex-col items-start gap-0.5 rounded-md transition-all duration-150',
        SIZE_CLASS[size],
        isClickable &&
          'group/route cursor-pointer px-2.5 py-1.5 -mx-1 border border-transparent hover:border-blue-200/80 hover:bg-blue-50 hover:shadow-sm active:bg-blue-100 active:border-blue-300/80',
        className,
      )}
      onClick={isClickable ? handleClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      title={isClickable ? t('m-order:table.route_cell.edit_hint') : undefined}
      onKeyDown={
        isClickable
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                if (order) onClick?.(order);
              }
            }
          : undefined
      }>
      <div className="flex min-w-0 max-w-full items-center gap-0.5">
        {originInaccurate ? <InaccurateLocationHint /> : null}
        {routeStops.length > 0 ? (
          <RegionLane
            stops={routeStops}
            separator={routeArrow}
            className={cn(
              'inline-flex min-w-0 items-center gap-1 font-medium text-gray-900',
              isClickable && 'group-hover/route:text-primary',
            )}
          />
        ) : null}
        {destinationInaccurate ? <InaccurateLocationHint /> : null}
      </div>
      {pickupDateLine ? (
        <span
          className={cn(
            'w-full truncate text-start text-xs text-muted-foreground',
            isClickable && 'group-hover/route:text-primary/80',
          )}>
          {pickupDateLine.date} · {pickupDateLine.time}
        </span>
      ) : null}
    </div>
  );
};
